import React, { useState, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Sun, Battery, Zap, FileText } from 'lucide-react';

const SolarQuoteDescription = ({ onDescriptionChange, initialDescription }) => {
  const [description, setDescription] = useState(initialDescription || '');
  const [productType, setProductType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customNotes, setCustomNotes] = useState('');

  const solarTags = [
    'Panneau monocristallin',
    'Onduleur',
    'Batterie lithium-ion',
    'Structure de montage',
  ];

  const mockDatabase = [
    { id: 1, name: 'Panneau JA Solar 540W bifacial', type: 'panel', power: 540, efficiency: 21.3 },
    { id: 2, name: 'Onduleur Growatt 5kW', type: 'inverter', power: 5000, efficiency: 98.4 },
    { id: 3, name: 'Batterie Lithium-Ion 10kWh', type: 'battery', capacity: 10, depthOfDischarge: 90 },
  ];

  useEffect(() => {
    if (searchTerm.length > 2) {
      setSuggestions(
        mockDatabase.filter(product =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setSuggestions([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    onDescriptionChange(description);
  }, [description, onDescriptionChange]);

  const handleProductSelect = (product) => {
    setSearchTerm(product.name);
    setSuggestions([]);
    setProductType(product.type);

    let newDescription = `${product.name}\n`;
    if (product.type === 'panel') {
      newDescription += `Puissance: ${product.power}W, Efficacité: ${product.efficiency}%\n`;
    } else if (product.type === 'inverter') {
      newDescription += `Puissance nominale: ${product.power}W, Efficacité: ${product.efficiency}%\n`;
    } else if (product.type === 'battery') {
      newDescription += `Capacité: ${product.capacity}kWh, Profondeur de décharge: ${product.depthOfDischarge}%\n`;
    }
    newDescription += 'Garantie: 25 ans, Certifié CE ISO 9001 et TUV\n';
    newDescription += 'Support et Maintenance inclus pendant 2 ans, avec assistance technique 24/7\n';

    setDescription(newDescription);
  };

  const calculateSystemBenefits = () => {
    const totalPower = 5000;
    const dailyProduction = (totalPower * 5.5) / 1000;
    const annualProduction = dailyProduction * 365;
    const co2Reduction = (annualProduction * 0.5) / 1000;
    const annualSavings = annualProduction * 136.49;
    const roi = (totalPower * 1000) / annualSavings;

    return `Capacité de Production: Système de ${totalPower / 1000} kWc capable de produire jusqu'à ${dailyProduction.toFixed(2)} kWh par jour.\n` +
           `Réduction de l'Empreinte Carbone: Contribue à une réduction annuelle de ${co2Reduction.toFixed(2)} tonnes de CO2.\n` +
           `Économies d'Énergie: Économies estimées de ${annualSavings.toFixed(0)} FCFA par an, avec un retour sur investissement en ${roi.toFixed(1)} ans.`;
  };

  return (
    <div className="space-y-4">
      <div className="flex space-x-2">
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un produit"
          className="flex-grow"
        />
        <Button
          onClick={() => setIsModalOpen(true)}
          style={{ backgroundColor: '#E6E6FA', color: '#000' }}
        >
          <FileText className="mr-2 h-4 w-4" /> Notes
        </Button>
      </div>

      {suggestions.length > 0 && (
        <ul className="bg-white border rounded-md mt-1">
          {suggestions.map(product => (
            <li
              key={product.id}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleProductSelect(product)}
            >
              {product.name}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        {solarTags.map(tag => (
          <Button
            key={tag}
            onClick={() => setSelectedTags([...selectedTags, tag])}
            variant="outline"
            style={{
              backgroundColor: '#D1E7FF',
              borderColor: '#B3D7FF',
              color: '#0056B3',
            }}
          >
            {tag}
          </Button>
        ))}
      </div>

      <Editor
        apiKey="yyfuzb9fa2uuwnh2vh3hqnr00ikb3ndz56e6fv3rztzaqda5"
        value={description}
        onEditorChange={setDescription}
        init={{
          height: 200,
          menubar: false,
          plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen',
            'insertdatetime media table paste code help wordcount'
          ],
          toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
        }}
      />

      <div className="flex justify-between">
        <Button
          onClick={() => setDescription(prev => `${calculateSystemBenefits()}\n\n${prev}`)}
          style={{
            backgroundColor: '#D1E7FF',
            borderColor: '#B3D7FF',
            color: '#0056B3',
          }}
        >
          <Sun className="mr-2 h-4 w-4" /> Avantages du Système
        </Button>

        <Button
          onClick={() => setDescription(prev => `${prev}\n\nCaractéristiques techniques:\n- Haute efficacité\n- Performance optimale\n- Fiabilité éprouvée`)}
          style={{
            backgroundColor: '#D1E7FF',
            borderColor: '#B3D7FF',
            color: '#0056B3',
          }}
        >
          <Zap className="mr-2 h-4 w-4" /> Caractéristiques Techniques
        </Button>

        <Button
          onClick={() => setDescription(prev => `${prev}\n\nGarantie: 25 ans sur les panneaux, 10 ans sur l'onduleur\nCertifications: CE, ISO 9001, TUV`)}
          style={{
            backgroundColor: '#D1E7FF',
            borderColor: '#B3D7FF',
            color: '#0056B3',
          }}
        >
          <Battery className="mr-2 h-4 w-4" /> Garantie et Certifications
        </Button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg w-3/4 max-w-2xl">
            <h2 className="text-lg font-bold mb-2">Notes personnalisées</h2>
            <p className="mb-4">Ajoutez vos notes personnalisées ici...</p>
            <textarea
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              className="w-full h-32 p-2 border rounded"
              placeholder="Ajoutez vos notes personnalisées ici..."
            />
            <Button
              onClick={() => {
                setDescription(prev => `${prev}\n\nNotes personnalisées:\n${customNotes}`);
                setIsModalOpen(false);
              }}
            >
              Ajouter aux notes
            </Button>
            <Button onClick={() => setIsModalOpen(false)} className="ml-2">Fermer</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarQuoteDescription;
